/**
 * @file apps/web/src/components/DesignItemPanel.tsx
 * @description Slide-over panel for creating or editing a single Design
 * item. Edit mode pre-fills from the target item and also exposes status.
 * Create mode starts blank (type defaults to 'other', status is fixed to
 * 'draft' server-side — not editable here). Build-phase fields (build_status/
 * build_notes/built_by/built_at) are edited on the Build page, not here.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Save, Loader2, AlertCircle, ExternalLink } from 'lucide-react';
import { Badge, type BadgeVariant } from './Badge.js';

// ---------------------------------------------------------------------------
// Types — snake_case, mirrors the raw Postgres row the API returns
// (no camelCase serialization layer exists in this codebase's API responses).
// ---------------------------------------------------------------------------

export interface DesignItem {
  id:                string;
  project_id:        string;
  discovery_node_id: string | null;
  title:             string;
  description:       string | null;
  type:              string;
  status:            string;
  module_tag:        string | null;
  process_area:      string | null;
  build_status:      string;
  build_notes:       string | null;
  built_by:          string | null;
  built_by_name:     string | null;
  built_at:          string | null;
}

export type DesignItemPanelTarget =
  | { mode: 'create' }
  | { mode: 'edit'; item: DesignItem };

interface DesignItemPanelProps {
  target:    DesignItemPanelTarget | null;
  projectId: string;
  onClose:   () => void;
  onCreate:  (input: { title: string; description: string; type: string }) => Promise<void>;
  onUpdate:  (itemId: string, input: { title: string; description: string; type: string; status: string }) => Promise<void>;
}

const TYPE_OPTIONS = [
  'suiteflow', 'suitescript', 'custom_field', 'suiteapp', 'bundle', 'configuration', 'other',
];
const STATUS_OPTIONS = ['draft', 'in_review', 'approved'];

// ---------------------------------------------------------------------------
// DesignItemPanel
// ---------------------------------------------------------------------------

export function DesignItemPanel({ target, projectId, onClose, onCreate, onUpdate }: DesignItemPanelProps) {
  const [title, setTitle]             = useState('');
  const [description, setDescription] = useState('');
  const [type, setType]               = useState('other');
  const [status, setStatus]           = useState('draft');
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState<string | null>(null);

  useEffect(() => {
    if (!target) return;
    if (target.mode === 'edit') {
      setTitle(target.item.title);
      setDescription(target.item.description ?? '');
      setType(target.item.type);
      setStatus(target.item.status);
    } else {
      setTitle('');
      setDescription('');
      setType('other');
      setStatus('draft');
    }
    setError(null);
  }, [target]);

  if (!target) return null;

  const isEdit = target.mode === 'edit';

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await onUpdate(target.item.id, { title: title.trim(), description, type, status });
      } else {
        await onCreate({ title: title.trim(), description, type });
      }
      setError(null);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save design item');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-ink/20 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col">
        <div className="flex items-start justify-between gap-3 p-5 border-b border-[rgba(8,17,31,0.08)]">
          <div className="min-w-0">
            <p className="text-[11px] font-mono text-muted">
              {isEdit ? 'Edit Design Item' : 'New Design Item'}
            </p>
            {isEdit && (target.item.module_tag || target.item.process_area) && (
              <p className="text-[11px] font-mono text-muted/70 mt-0.5">
                {[target.item.module_tag, target.item.process_area].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
          <button onClick={onClose} className="btn-icon shrink-0" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {isEdit && target.item.discovery_node_id && (
            <Link
              to={`/projects/${projectId}/discovery`}
              className="flex items-center gap-1.5 text-xs text-blue hover:underline w-fit"
            >
              <ExternalLink size={12} /> From Discovery
            </Link>
          )}

          <div>
            <label className="label mb-1.5">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Vendor Bill Approval Workflow"
              className="input text-sm"
              autoFocus
            />
          </div>

          <div>
            <label className="label mb-1.5">Type</label>
            <div className="flex flex-wrap gap-1.5">
              {TYPE_OPTIONS.map((value) => (
                <button
                  key={value}
                  onClick={() => setType(value)}
                  className={[
                    'text-xs font-mono px-2.5 py-1.5 rounded-full border transition-colors',
                    type === value
                      ? 'border-blue bg-sky text-blue'
                      : 'border-[rgba(8,17,31,0.12)] text-muted hover:text-ink',
                  ].join(' ')}
                >
                  <Badge variant={value as BadgeVariant} size="xs" className={type === value ? '' : 'opacity-60'} />
                </button>
              ))}
            </div>
          </div>

          {isEdit && (
            <div>
              <label className="label mb-1.5">Status</label>
              <div className="flex flex-wrap gap-1.5">
                {STATUS_OPTIONS.map((value) => (
                  <button
                    key={value}
                    onClick={() => setStatus(value)}
                    className={[
                      'text-xs font-mono px-2.5 py-1.5 rounded-full border transition-colors',
                      status === value
                        ? 'border-blue bg-sky text-blue'
                        : 'border-[rgba(8,17,31,0.12)] text-muted hover:text-ink',
                    ].join(' ')}
                  >
                    <Badge variant={value as BadgeVariant} size="xs" className={status === value ? '' : 'opacity-60'} />
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="label mb-1.5">Notes</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this design item covers, decisions made…"
              className="input text-sm min-h-[120px] resize-none"
              rows={5}
            />
          </div>
        </div>

        <div className="p-5 border-t border-[rgba(8,17,31,0.08)] space-y-3">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm">
              <AlertCircle size={14} className="shrink-0" /> {error}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="btn-ghost text-sm" disabled={saving}>Cancel</button>
            <button onClick={() => void handleSave()} disabled={saving} className="btn-primary text-sm">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
